import styled from 'styled-components'

const focusRing = ({ theme }: any) => theme.isDark ? theme.shadows.focusDark : theme.shadows.focus

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  min-width: 0;
`

export const BackLink = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.accent[100]};
  font: inherit;
  font-size: 15px;
  line-height: 20px;
  cursor: pointer;

  img, svg {
    width: 16px;
    height: 16px;
    transform: rotate(90deg);
  }

  &:hover { text-decoration: underline; }
  &:focus-visible { outline: none; box-shadow: ${focusRing}; border-radius: 4px; }
`

export const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.palette[900]};
  font-size: 26px;
  line-height: 32px;
  font-weight: 500;
`

export const Modes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
`

const ring = (theme: any, on?: boolean, width = on ? 2 : 1) =>
  `inset 0 0 0 ${width}px ${on ? theme.accent[100] : theme.grey[400]}`

export const ModeCard = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme, $active }) => $active ? theme.primary[25] : theme.base.white};
  box-shadow: ${({ theme, $active }) => ring(theme, $active)};
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:disabled { cursor: default; opacity: 0.6; }
  &:focus-visible { outline: none; box-shadow: ${({ theme, $active }) => `${ring(theme, $active)}, ${focusRing({ theme })}`}; }
`

export const Radio = styled.span<{ $checked?: boolean }>`
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  box-sizing: border-box;
  border-radius: 50%;
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme, $checked }) => `inset 0 0 0 ${$checked ? 5 : 1}px ${$checked ? theme.accent[100] : theme.grey[400]}`};
`

export const ModeText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const ModeTitle = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
`

export const Small = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 13px;
  line-height: 18px;
`

export const Steps = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
`

export const StepButton = styled.button<{ $state: 'done' | 'current' | 'next' }>`
  appearance: none;
  display: flex;
  flex: 1 1 140px;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 0;
  border: 0;
  border-bottom: 2px solid ${({ theme, $state }) => $state === 'next' ? theme.grey[350] : theme.accent[100]};
  background: transparent;
  color: ${({ theme, $state }) => $state === 'next' ? theme.grey[700] : theme.palette[900]};
  font: inherit;
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  text-align: left;
  cursor: ${({ $state }) => $state === 'done' ? 'pointer' : 'default'};

  &:focus-visible { outline: none; box-shadow: ${focusRing}; }
`

export const StepBadge = styled.span<{ $state: 'done' | 'current' | 'next' }>`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ theme, $state }) => $state === 'done' ? theme.accent[100] : $state === 'current' ? theme.accent[15] : theme.grey[300]};
  color: ${({ theme, $state }) => $state === 'done' ? '#FFFFFF' : $state === 'current' ? '#0F7BE6' : theme.grey[700]};
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
`

export const Layout = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px;
  margin-top: 6px;
`

export const Main = styled.div`
  display: flex;
  flex: 1 1 560px;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

export const Card = styled.section<{ $error?: boolean; $flush?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $flush }) => $flush ? '0' : '16px'};
  min-width: 0;
  padding: ${({ $flush }) => $flush ? '0' : '20px'};
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme, $error }) => `inset 0 0 0 1px ${$error ? theme.errors[600] : theme.grey[350]}, ${theme.shadows.control}`};

  @media (max-width: 768px) {
    padding: ${({ $flush }) => $flush ? '0' : '14px'};
  }
`

export const CardHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

export const CardTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.palette[900]};
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
`

export const Aside = styled.span`
  color: ${({ theme }) => theme.grey[700]};
  font-weight: 400;
`

export const Hint = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 14px;
  line-height: 20px;
`

export const ErrorLine = styled.span`
  color: ${({ theme }) => theme.errors[600]};
  font-size: 13px;
  line-height: 18px;
`

export const TwoCols = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`

export const PickRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  align-items: start;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`

export const Chip = styled.button<{ $active?: boolean }>`
  appearance: none;
  height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme, $active }) => $active ? theme.accent[15] : theme.grey[100]};
  box-shadow: ${({ theme, $active }) => `inset 0 0 0 1px ${$active ? theme.accent[100] : theme.grey[400]}`};
  color: ${({ theme, $active }) => $active ? '#0F7BE6' : theme.isDark ? theme.palette[900] : theme.grey[800]};
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  &:focus-visible { outline: none; box-shadow: ${focusRing}; }
`

export const SegmentGroup = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 2px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.palette[200]};
`

export const Segment = styled.button<{ $active?: boolean }>`
  appearance: none;
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: ${({ theme, $active }) => $active ? theme.base.white : 'transparent'};
  box-shadow: ${({ theme, $active }) => $active ? theme.shadows.control : 'none'};
  color: ${({ theme, $active }) => $active ? theme.palette[900] : theme.isDark ? theme.neutral[80] : theme.grey[600]};
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${({ $active }) => $active ? 500 : 400};
  white-space: nowrap;
  cursor: pointer;

  &:hover { color: ${({ theme }) => theme.palette[900]}; }
  &:focus-visible { outline: none; box-shadow: ${focusRing}; }
`

export const OsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
  gap: 8px;
`

export const OsTile = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 6px 12px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme, $active }) => $active ? theme.primary[25] : theme.base.white};
  box-shadow: ${({ theme, $active }) => ring(theme, $active)};
  color: ${({ theme }) => theme.palette[900]};
  font: inherit;
  font-size: 14px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;

  img {
    flex: none;
    width: 24px;
    height: 24px;
  }

  &:focus-visible { outline: none; box-shadow: ${({ theme, $active }) => `${ring(theme, $active)}, ${focusRing({ theme })}`}; }
`

export const Mono = styled.span`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${({ theme }) => theme.grey[300]};
  color: ${({ theme }) => theme.grey[600]};
  font-size: 10px;
  font-weight: 600;
`

export const StockHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 20px 20px 14px;

  @media (max-width: 768px) {
    padding: 14px 14px 10px;
  }
`

export const StockColumns = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(80px, 1fr) minmax(120px, 1.3fr) 70px minmax(130px, 1.4fr) minmax(120px, 1fr);
  align-items: center;
  height: 34px;
  background: ${({ theme }) => theme.palette[200]};
  color: ${({ theme }) => theme.grey[600]};
  font-size: 12px;
  line-height: 1;

  @media (max-width: 1100px) {
    display: none;
  }
`

export const StockRow = styled.button<{ $active?: boolean }>`
  appearance: none;
  display: grid;
  grid-template-columns: 36px minmax(80px, 1fr) minmax(120px, 1.3fr) 70px minmax(130px, 1.4fr) minmax(120px, 1fr);
  align-items: center;
  gap: 4px 0;
  width: 100%;
  min-height: 60px;
  padding: 8px 0;
  border: 0;
  border-top: ${({ theme }) => `1px solid ${theme.grey[300]}`};
  background: ${({ theme, $active }) => $active ? theme.primary[25] : 'transparent'};
  box-shadow: ${({ theme, $active }) => `inset 3px 0 0 ${$active ? theme.accent[100] : 'transparent'}`};
  color: ${({ theme }) => theme.palette[900]};
  font: inherit;
  font-size: 15px;
  line-height: 20px;
  text-align: left;
  cursor: pointer;

  > * { padding-right: 10px; min-width: 0; }
  > :first-child { grid-row: span 2; display: flex; justify-content: center; padding-right: 0; }

  &:hover { background: ${({ theme, $active }) => $active ? theme.primary[25] : theme.grey[100]}; }
  &:focus-visible { outline: none; box-shadow: ${({ theme }) => `inset 0 0 0 2px ${theme.accent[100]}`}; }

  @media (max-width: 1100px) {
    grid-template-columns: 44px minmax(0, 1fr);
    > :first-child { grid-row: span 5; align-self: start; padding-top: 2px; }
  }
`

export const StockName = styled.span`
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const StockPrice = styled.span`
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const DiskChip = styled.span<{ $accent?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme, $accent }) => $accent ? theme.accent[15] : theme.palette[200]};
  color: ${({ theme, $accent }) => $accent ? '#0F7BE6' : theme.isDark ? theme.palette[900] : theme.grey[800]};
  font-size: 13px;
  line-height: 16px;
  white-space: nowrap;
`

export const DiskList = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`

export const Faint = styled.span`
  color: ${({ theme }) => theme.grey[700]};
  font-size: 13px;
  line-height: 18px;
  white-space: nowrap;
`

export const StockFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: ${({ theme }) => `1px solid ${theme.grey[300]}`};
  border-radius: ${({ theme }) => `0 0 ${theme.radius.control} ${theme.radius.control}`};
  background: ${({ theme }) => theme.grey[100]};
  color: ${({ theme }) => theme.isDark ? theme.palette[900] : theme.grey[800]};
  font-size: 15px;
  line-height: 20px;

  > span { margin-right: auto; }

  @media (max-width: 768px) {
    padding: 14px;
  }
`

export const DiskRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 10px 10px 12px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.grey[100]};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[300]}`};

  > :nth-child(2) { flex: 1; min-width: 0; }
`

export const DiskIndex = styled.span`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 40px;
  color: ${({ theme }) => theme.grey[700]};
  font-size: 13px;
`

export const IconSquare = styled.button`
  appearance: none;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[400]}`};
  cursor: pointer;

  img { width: 18px; height: 18px; }

  &:hover { box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.errors[600]}`}; }
  &:focus-visible { outline: none; box-shadow: ${focusRing}; }
`

export const AddDisk = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 44px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  background: transparent;
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[400]}`};
  color: ${({ theme }) => theme.accent[100]};
  font: inherit;
  font-size: 15px;
  cursor: pointer;

  img { width: 18px; height: 18px; }

  &:hover:not(:disabled) { background: ${({ theme }) => theme.primary[25]}; box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.accent[100]}`}; }
  &:disabled { opacity: 0.5; cursor: default; }
  &:focus-visible { outline: none; box-shadow: ${focusRing}; }
`

export const SplitLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.palette[900]};
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
`

export const TextArea = styled.textarea`
  min-height: 84px;
  padding: 10px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[400]}`};
  color: ${({ theme }) => theme.palette[900]};
  font: inherit;
  font-size: 15px;
  line-height: 20px;
  resize: vertical;
  outline: none;

  &::placeholder { color: ${({ theme }) => theme.grey[700]}; }
  &:focus { box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.accent[100]}, ${focusRing({ theme })}`}; }
`

export const InfoNote = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.isDark ? 'rgba(40, 148, 255, 0.14)' : theme.accent[15]};
  color: ${({ theme }) => theme.isDark ? theme.accent[100] : '#0F7BE6'};
  font-size: 14px;
  line-height: 20px;
`

export const ReviewCard = styled(Card)`
  gap: 0;
  padding: 6px 20px;

  @media (max-width: 768px) {
    padding: 6px 14px;
  }
`

export const ReviewHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0 8px;
`

export const EditLink = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.accent[100]};
  font: inherit;
  font-size: 14px;
  cursor: pointer;

  img { width: 16px; height: 16px; }
  &:focus-visible { outline: none; box-shadow: ${focusRing}; border-radius: 4px; }
`

export const ReviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  padding: 10px 0;
  border-top: ${({ theme }) => `1px solid ${theme.grey[300]}`};

  > :first-child {
    flex: 0 0 160px;
    color: ${({ theme }) => theme.grey[600]};
    font-size: 14px;
    line-height: 20px;
  }

  > :last-child {
    flex: 1 1 200px;
    color: ${({ theme }) => theme.palette[900]};
    font-size: 15px;
    line-height: 20px;
    overflow-wrap: anywhere;
    white-space: pre-line;
  }
`

export const Summary = styled.aside`
  position: sticky;
  top: 24px;
  display: flex;
  flex: 1 1 300px;
  flex-direction: column;
  max-width: 340px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.base.white};
  box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.grey[350]}, ${theme.shadows.control}`};

  @media (max-width: 920px) {
    position: static;
    max-width: none;
  }
`

export const SummaryHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 16px 20px 12px;
`

export const Eyebrow = styled.span`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 12px;
  line-height: 1;
  text-transform: uppercase;
`

export const SummaryTitle = styled.span`
  color: ${({ theme }) => theme.palette[900]};
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const SummaryRow = styled.div<{ $empty?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 20px;
  border-top: ${({ theme }) => `1px solid ${theme.grey[300]}`};

  > span + span {
    color: ${({ theme, $empty }) => $empty ? theme.grey[400] : theme.palette[900]};
    font-size: 15px;
    line-height: 20px;
    overflow-wrap: anywhere;
  }
`

export const SummaryActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  border-top: ${({ theme }) => `1px solid ${theme.grey[350]}`};

  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
`

export const Problem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.control};
  background: ${({ theme }) => theme.errors[100]};
  color: ${({ theme }) => theme.errors[600]};
  font-size: 14px;
  line-height: 20px;
  white-space: pre-line;
`
